import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import arialFont from './ARIAL.TTF';

// Register Arial font with jsPDF if not already registered
if (jsPDF && jsPDF.API && jsPDF.API.addFileToVFS && jsPDF.API.addFont) {
  const fontList = jsPDF.API.getFontList();
  if (!fontList['Arial'] || !fontList['Arial'].includes('normal')) {
    jsPDF.API.addFileToVFS('Arial.ttf', arialFont);
    jsPDF.API.addFont('Arial.ttf', 'Arial', 'normal');
  }
}

// Helper to get school info from localStorage and convert logo to base64
const getSchoolInfo = async () => {
  const stored = localStorage.getItem('schoolInfo');
  if (!stored) return { name: '', address: '', logo: '', phone: '', email: '' };

  const school = JSON.parse(stored);
  let base64Logo = '';

  // if (school.logo) {
  //   try {
  //     // فقط قم بالتحويل إذا لم تكن الصورة بالفعل بصيغة base64
  //     if (school.logo.startsWith('data:image')) {
  //       base64Logo = school.logo;
  //     } else {
  //       const res = await fetch(school.logo);
  //       const blob = await res.blob();
  //       base64Logo = await new Promise((resolve, reject) => {
  //         const reader = new FileReader();
  //         reader.onloadend = () => resolve(reader.result);
  //         reader.onerror = reject;
  //         reader.readAsDataURL(blob);
  //       });
  //     }
  //   } catch (error) {
  //     console.error('فشل تحميل أو تحويل اللوجو:', error);
  //     base64Logo = '';
  //   }
  // }

  return {
    logo: school.logo,
    name: school.name || '',
    address: school.address || '',
    phone: school.phone_number || '',
    email: school.email || '',
  };
};


// Helper to get user info from localStorage
function getCurrentUserName() {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?.full_name || 'اليوزر الحالي';
  } catch {
    return '';
  }
}

// Helper to get school phone (handles nested structure)
function getSchoolPhone(schoolInfo) {
  // يدعم schoolInfo.phone_number أو schoolInfo.contact?.phone
  return schoolInfo.phone || (schoolInfo.contact && schoolInfo.contact.phone) || '';
}

export const exportToExcel = (data, columns, filename = 'data') => {
  const worksheetData = [columns.map(col => col.header), ...data.map(row => columns.map(col => row[col.key]))];
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(blob, `${filename}.xlsx`);
};

// Helper: reverse Arabic text for jsPDF (quick fix for RTL)
function fixArabic(str) {
  if (!str) return '';
  // Only reverse if contains Arabic
  const arabic = /[\u0600-\u06FF]/;
  if (!arabic.test(str)) return str;
  return str.split('').reverse().join('');
}

export const exportToPDF = async (data, columns, filename = 'document', options = {}) => {
  const schoolInfo = await getSchoolInfo();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Set RTL and use Arial font for Arabic
  doc.setR2L && doc.setR2L(true);
  doc.setFont('Arial');

  // Modern Header (RTL, Arabic)
  let y = 40;
  try {
    if (schoolInfo.logo) {
      doc.addImage(schoolInfo.logo, 'PNG', pageWidth - 110, y, 80, 60);
    }
  } catch (e) {
    // Ignore logo if it's invalid or corrupt
    console.warn('تعذر إضافة الشعار إلى PDF:', e);
  }
  doc.setFont('Arial');
  doc.setFontSize(22);
  doc.setTextColor('#4d4cac');
  doc.text(fixArabic(schoolInfo.name || ''), pageWidth - 60, y + 10, { align: 'right' });
  doc.setFont('Arial');
  doc.setFontSize(12);
  doc.setTextColor('#333');//schoolInfo.address || ''
  doc.text(fixArabic(`العنوان: ${schoolInfo.address}`), pageWidth - 60, y + 35, { align: 'right' });
  if (schoolInfo.phone_number) doc.text(fixArabic(`هاتف: ${schoolInfo.phone_number}`), pageWidth - 60, y + 55, { align: 'right' });
  if (schoolInfo.email) doc.text(fixArabic(`البريد الإلكتروني: ${schoolInfo.email}`), pageWidth - 60, y + 75, { align: 'right' });

  // Custom Title (e.g. class name)
  if (options && options.title) {
    doc.setFont('Arial');
    doc.setFontSize(18);
    doc.setTextColor('#1976d2');
    doc.text(fixArabic(options.title), pageWidth / 2, y + 100, { align: 'center' });
  }

  // Table with modern style
  autoTable(doc, {
    startY: y + 120,
    head: [columns.map(col => fixArabic(col.header))],
    body: data.map(row => columns.map(col => fixArabic(row[col.key]))),
    styles: {
      font: 'Arial',
      fontSize: 11,
      cellPadding: 7,
      textColor: '#222',
      fillColor: [255, 255, 255],
      lineColor: [77, 76, 172],
      lineWidth: 0.5,
      halign: 'right',
    },
    headStyles: {
      fillColor: [77, 76, 172],
      textColor: '#fff',
      fontStyle: 'bold',
      fontSize: 13,
      halign: 'center',
      font: 'Arial',
    },
    alternateRowStyles: {
      fillColor: [247, 249, 250],
    },
    margin: { left: 40, right: 40 },
    tableLineColor: [77, 76, 172],
    tableLineWidth: 0.5,
    didDrawPage: (data) => {
      doc.setFont('Arial');
      doc.setFontSize(10);
      doc.setTextColor('#888');
      doc.text(fixArabic(`تاريخ التصدير: ${new Date().toLocaleDateString('en-GB')}`), pageWidth - 60, pageHeight - 20, { align: 'right' });
    },
  });

  doc.save(`${filename}.pdf`);
};

export function exportStudentsListToPrint(students, classMap, bus) {
  if (!students || students.length === 0) return;

  const mainColor = '#4d4cac';
  const headerBg = '#f5f7fa';
  const subHeaderBg = '#f0f4fa';
  const borderColor = '#bfc4d1';
  const rowAlt = '#f9fafa';

  // Bus info header
  const busInfoHtml = `
    <table style="width:100%; border-collapse:separate; border-spacing:0; margin-bottom:24px; font-size:1.1rem; background:#fff; border-radius:12px; box-shadow:0 2px 12px #4d4cac11; overflow:hidden;">
      <tr><th colspan="4" style="background:${mainColor}; color:#fff; border:1px solid ${borderColor}; font-size:1.2rem; padding:12px 8px; border-radius:12px 12px 0 0; text-align:center; letter-spacing:1px;">معلومات الحافلة</th></tr>
      <tr style="background:${headerBg};">
        <td style="border:1px solid ${borderColor}; padding:8px 10px; font-weight:bold;">رقم اللوحة</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px;">${bus.bus_number || ''}</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px; font-weight:bold;">اسم السائق</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px;">${ bus.bus_type === 'داخلي' ? bus.driver_name : bus.manager_name }</td>
      </tr>
      <tr style="background:${rowAlt};">
        <td style="border:1px solid ${borderColor}; padding:8px 10px; font-weight:bold;">النوع</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px;">${bus.bus_type === 'Internal' || bus.bus_type === 'داخلي' ? 'داخلي' : 'خارجي'}</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px; font-weight:bold;">عدد الطلاب</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px;">${students.length} طالب</td>
      </tr>
    </table>
  `;

  // Students table
  let studentsRows = '';
  students.forEach((student, idx) => {
    studentsRows += `
      <tr style="background:${idx%2===0?rowAlt:'#fff'};">
        <td style='border:1px solid ${borderColor}; padding:10px 8px; text-align:center;'>${idx + 1}</td>
        <td style='border:1px solid ${borderColor}; padding:10px 8px;'>${(student.first_name || '') + ' ' + (student.second_name || '')}</td>
        <td style='border:1px solid ${borderColor}; padding:10px 8px; text-align:center;'>${classMap.get(student.school_class) || 'غير محدد'}</td>
        <td style='border:1px solid ${borderColor}; padding:10px 8px; text-align:center;'>${student.student_id || '—'}</td>
        <td style='border:1px solid ${borderColor}; padding:10px 8px; text-align:center;'>${student.parent_phone || '—'}</td>
        <td style='border:1px solid ${borderColor}; padding:10px 8px; text-align:center;'>${student.parent_phone_2 || '—'}</td>
                <td style='border:1px solid ${borderColor}; padding:10px 8px; text-align:center;'>${student.address || '—'}</td>

      </tr>
    `;
  });

  const studentsTableHtml = `
    <table style="width:100%; border-collapse:separate; border-spacing:0; margin-bottom:0; font-size:1.05rem; background:#fff; border-radius:12px; box-shadow:0 2px 12px #4d4cac11; overflow:hidden;">
      <tr><th colspan="7" style="background:${mainColor}; color:#fff; border:1px solid ${borderColor}; font-size:1.15rem; padding:12px 8px; border-radius:12px 12px 0 0; text-align:center; letter-spacing:1px;">قائمة الطلاب المسجلين</th></tr>
      <tr style="background:${subHeaderBg};">
        <th style='border:1px solid ${borderColor}; padding:10px 8px; width:8%;'>#</th>
        <th style='border:1px solid ${borderColor}; padding:10px 8px; width:30%;'>اسم الطالب</th>
        <th style='border:1px solid ${borderColor}; padding:10px 8px; width:20%;'>الصف</th>
        <th style='border:1px solid ${borderColor}; padding:10px 8px; width:22%;'>رقم الهوية</th>
        <th style='border:1px solid ${borderColor}; padding:10px 8px; width:22%;'>رقم هاتف 1</th>
        <th style='border:1px solid ${borderColor}; padding:10px 8px; width:22%;'>رقم هاتف 2</th>
        <th style='border:1px solid ${borderColor}; padding:10px 8px; width:20%;'>العنوان</th>
      </tr>
      ${studentsRows}
    </table>
  `;

  // Summary section
  const summaryHtml = `
    <div style="margin-top:24px; text-align:center; font-size:1.1rem; color:${mainColor}; font-weight:bold;">
      إجمالي عدد الطلاب: ${students.length} طالب
    </div>
  `;

  // Combine all HTML
  const fullHtml = `
    <div style='direction:rtl; font-family:Alexandria, Arial, sans-serif;'>
      <div style='font-size:2rem; font-weight:800; color:${mainColor}; margin-bottom:20px; text-align:center; letter-spacing:1px;'>قائمة طلاب الحافلة</div>
      ${studentsTableHtml}
      ${summaryHtml}
    </div>
  `;

  exportToPrintWindow(
    [],
    [],
    `قائمة طلاب الحافلة: ${bus.name || bus.bus_number || ''}`,
    { customHtml: fullHtml }
  );
}

export const exportToPrintWindow = async (data, columns, title = '', options = {}) => {
  const schoolInfo = await getSchoolInfo();
  const userName = getCurrentUserName();
  const schoolPhone = getSchoolPhone(schoolInfo);
  
  // Handle both URL and base64 cases
  let logoSrc = schoolInfo.logo;
  if (logoSrc && !logoSrc.startsWith('data:') && !logoSrc.startsWith('http')) {
    // If it's base64 without data URI prefix, add it
    logoSrc = `data:image/png;base64,${logoSrc}`;
  }

  // Create logo HTML positioned at top right
  const logoHtml = logoSrc
    ? `<div style='position:absolute;top:16px;right:16px;width:80px;height:80px;border-radius:16px;border:2px solid #eaeaea;background-color:#fff;box-shadow:0 2px 8px #4d4cac22;display:flex;align-items:center;justify-content:center;overflow:hidden;'>
         <img src="${logoSrc}" style="max-width:100%;max-height:100%;object-fit:contain;" onload="this.style.opacity=1" onerror="this.style.display='none'" />
       </div>`
    : '';

  const schoolHeader = `
    <div style="width:100%;text-align:center;padding:24px 16px 16px 16px;background:#f4f6fa;border-radius:16px 16px 0 0;border-bottom:3px solid #d3d9e6;position:relative;min-height:120px;">
      ${logoHtml}
      <div style="font-size:2.2rem;font-weight:800;color:#4d4cac;margin-bottom:8px;padding-right:100px;">${schoolInfo.name || ''}</div>
      <div style="font-size:1.05rem;color:#444;">${'العنوان: ' + schoolInfo.address || ''}</div>
      <div style="font-size:1rem;color:#666;">${schoolPhone ? 'هاتف المدرسة: ' + schoolPhone : ''}</div>
      <div style="font-size:1rem;color:#666;">${schoolInfo.email ? 'البريد الإلكتروني: ' + schoolInfo.email : ''}</div>
    </div>
  `;

  const printFooter = `
    <div style="margin-top:36px;text-align:left;color:#888;font-size:0.95rem;">
      <span>تاريخ الطباعة: ${new Date().toLocaleDateString('en-GB')}</span><br/>
      <span>المستخدم: ${userName || '-'} </span>
    </div>
  `;

  // دعم customHtml للطباعة المتقدمة (عدة جداول/تفاصيل)
  let bodyContent = '';
  if (options && options.customHtml) {
    bodyContent = `
      ${schoolHeader}
      ${title ? `<h2>${title}</h2>` : ''}
      ${options.customHtml}
      ${printFooter}
    `;
  } else {
    const tableHtml = `
      <table style="width:100%;border-collapse:collapse;background:#fff;margin-top:16px;border-radius:12px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.05);">
        <thead>
          <tr style="background:#4d4cac;color:#fff;">
            ${columns.map(col => `<th style="padding:14px 10px;font-size:1rem;font-weight:600;border-bottom:2px solid #eaeaea;text-align:center;">${col.header}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${data.length === 0 ? `<tr><td colspan="${columns.length}" style="text-align:center;padding:32px 0;color:#999;font-size:1.1rem;">لا توجد بيانات</td></tr>` :
            data.map((row, idx) =>
              `<tr style="background:${idx%2===0?'#f9fafa':'#ffffff'};">
                ${columns.map(col => `<td style="padding:12px 8px;font-size:0.95rem;color:#333;text-align:center;border-bottom:1px solid #eee;">${row[col.key] ?? ''}</td>`).join('')}
              </tr>`
            ).join('')}
        </tbody>
      </table>`;

    // ملخص اختياري: عدد السندات ومجموع المبالغ
    let summaryHtml = '';
    if (options && options.summary) {
      const amountKey = options.summary.amountKey || 'amount';
      const count = data.length;
      const total = data.reduce((sum, row) => sum + (Number(row[amountKey]) || 0), 0);
      summaryHtml = `
        <div style="display:flex;gap:16px;margin-top:16px;justify-content:center;flex-wrap:wrap;">
          <div style="background:#f4f6fa;border-radius:12px;padding:12px 28px;text-align:center;border:1px solid #d3d9e6;">
            <div style="font-size:0.95rem;color:#666;">عدد السندات</div>
            <div style="font-size:1.4rem;font-weight:800;color:#4d4cac;">${count}</div>
          </div>
          <div style="background:#f4f6fa;border-radius:12px;padding:12px 28px;text-align:center;border:1px solid #d3d9e6;">
            <div style="font-size:0.95rem;color:#666;">مجموع المبالغ</div>
            <div style="font-size:1.4rem;font-weight:800;color:#4d4cac;">${total.toLocaleString('en-US')} شيكل</div>
          </div>
        </div>
      `;
    }

    bodyContent = `
      ${schoolHeader}
      ${title ? `<h2>${title}</h2>` : ''}
      ${tableHtml}
      ${summaryHtml}
      ${printFooter}
    `;
  }

  const html = `
    <html dir="rtl">
      <head>
        <title>طباعة</title>
        <style>
          body {
            font-family: 'Alexandria', Arial, sans-serif;
            background: #f4f6fa;
            margin: 0;
            padding: 32px;
            color: #333;
          }
          h2 {
            color: #4d4cac;
            font-size: 1.8rem;
            font-weight: 700;
            margin-bottom: 20px;
            text-align: center;
            letter-spacing: 1px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border-radius: 6px;
          }
          /* Ensure images load properly */
          div[style*="background:url"] {
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
          }
          @media print {
            body {
              background: #fff;
              padding: 0;
            }
            table {
              box-shadow: none !important;
            }
            .no-print {
              display: none;
            }
            /* Print-specific logo styles */
            div[style*="background:url"] {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        ${bodyContent}
      </body>
    </html>
  `;

  const printWindow = window.open('', '', 'width=900,height=650');
  printWindow.document.write(html);
  printWindow.document.close();
  
  // Wait for images to load before printing
  printWindow.onload = function() {
    // Create a promise that resolves when all images are loaded
    const images = printWindow.document.querySelectorAll('div[style*="background:url"]');
    let loadedCount = 0;
    const totalImages = images.length;
    
    if (totalImages === 0) {
      setTimeout(() => printWindow.print(), 350);
      return;
    }
    
    // For background images, we need to wait a bit longer
    setTimeout(() => printWindow.print(), 1000);
  };
};

export function exportBusDetailsToPrint(bus, assignedStudents, classMap) {
  if (!bus) return;
  // ألوان النظام
  const mainColor = '#4d4cac';
  const headerBg = '#f5f7fa';
  const subHeaderBg = '#f0f4fa';
  const borderColor = '#bfc4d1';
  const rowAlt = '#f9fafa';

  // 1. تفاصيل الباص
  const busDetailsHtml = `
    <table style="width:100%; border-collapse:separate; border-spacing:0; margin-bottom:28px; font-size:1.13rem; background:#fff; border-radius:12px; box-shadow:0 2px 12px #4d4cac11; overflow:hidden;">
      <tr><th colspan="4" style="background:${mainColor}; color:#fff; border:1px solid ${borderColor}; font-size:1.25rem; padding:12px 8px; border-radius:12px 12px 0 0; text-align:center; letter-spacing:1px;">تفاصيل الحافلة</th></tr>
      <tr style="background:${headerBg};">
        <td style="border:1px solid ${borderColor}; padding:8px 10px; font-weight:bold;">رقم اللوحة</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px;">${bus.bus_number || ''}</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px; font-weight:bold;">اسم السائق</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px;">${ bus.bus_type === 'داخلي' ? bus.driver_name : bus.manager_name }</td>
      </tr>
      <tr style="background:${rowAlt};">
        <td style="border:1px solid ${borderColor}; padding:8px 10px; font-weight:bold;">السعة</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px;">${bus.capacity ? bus.capacity + ' طالب' : ''}</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px; font-weight:bold;">عدد الطلاب</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px;">${assignedStudents.length} طالب</td>
      </tr>
      <tr style="background:${headerBg};">
        <td style="border:1px solid ${borderColor}; padding:8px 10px; font-weight:bold;">النوع</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px;">${bus.bus_type === 'Internal' || bus.bus_type === 'داخلي' ? 'داخلي' : 'خارجي'}</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px; font-weight:bold;">الهاتف</td>
        <td style="border:1px solid ${borderColor}; padding:8px 10px;">${bus.phone_number || bus.driver_phone || ''}</td>
      </tr>
    </table>
  `;

  // 2. جدول الصيانة والتكاليف
  let maintenanceRows = '';
  if (bus.payments && bus.payments.length > 0) {
    bus.payments.forEach((record, idx) => {
      maintenanceRows += `
        <tr style="background:${idx%2===0?rowAlt:'#fff'};">
          <td style='border:1px solid ${borderColor}; padding:8px 10px;'>${idx + 1}</td>
          <td style='border:1px solid ${borderColor}; padding:8px 10px;'>${record.type || 'غير محدد'}</td>
          <td style='border:1px solid ${borderColor}; padding:8px 10px;'>${record.amount || '0'} شيكل</td>
          <td style='border:1px solid ${borderColor}; padding:8px 10px;'>${record.reason || '—'}</td>
          <td style='border:1px solid ${borderColor}; padding:8px 10px;'>${formatDate(record.date)}</td>
        </tr>
      `;
    });
  } else {
    maintenanceRows = `<tr><td colspan='5' style='border:1px solid ${borderColor}; padding:12px; text-align:center; background:${rowAlt};'>لا يوجد سجل صيانة أو مدفوعات</td></tr>`;
  }
  const maintenanceTableHtml = `
    <table style="width:100%; border-collapse:separate; border-spacing:0; margin-bottom:28px; font-size:1.08rem; background:#fff; border-radius:12px; box-shadow:0 2px 12px #4d4cac11; overflow:hidden;">
      <tr><th colspan="5" style="background:${mainColor}; color:#fff; border:1px solid ${borderColor}; font-size:1.13rem; padding:10px 8px; border-radius:12px 12px 0 0; text-align:center; letter-spacing:1px;">سجل الصيانة والتكاليف</th></tr>
      <tr style="background:${subHeaderBg};">
        <th style='border:1px solid ${borderColor}; padding:8px 10px;'>#</th>
        <th style='border:1px solid ${borderColor}; padding:8px 10px;'>النوع</th>
        <th style='border:1px solid ${borderColor}; padding:8px 10px;'>المبلغ</th>
        <th style='border:1px solid ${borderColor}; padding:8px 10px;'>السبب</th>
        <th style='border:1px solid ${borderColor}; padding:8px 10px;'>التاريخ</th>
      </tr>
      ${maintenanceRows}
    </table>
  `;

  // 3. جدول الطلاب
  let studentsRows = '';
  if (assignedStudents.length > 0) {
    assignedStudents.forEach((student, idx) => {
      studentsRows += `
        <tr style="background:${idx%2===0?rowAlt:'#fff'};">
          <td style='border:1px solid ${borderColor}; padding:8px 10px;'>${idx + 1}</td>
          <td style='border:1px solid ${borderColor}; padding:8px 10px;'>${(student.first_name || '') + ' ' + (student.second_name || '')}</td>
          <td style='border:1px solid ${borderColor}; padding:8px 10px;'>${classMap.get(student.school_class) || 'غير محدد'}</td>
        </tr>
      `;
    });
  } else {
    studentsRows = `<tr><td colspan='3' style='border:1px solid ${borderColor}; padding:12px; text-align:center; background:${rowAlt};'>لا يوجد طلاب مسجلون في هذه الحافلة</td></tr>`;
  }
  const studentsTableHtml = `
    <table style="width:100%; border-collapse:separate; border-spacing:0; margin-bottom:0; font-size:1.08rem; background:#fff; border-radius:12px; box-shadow:0 2px 12px #4d4cac11; overflow:hidden;">
      <tr><th colspan="3" style="background:${mainColor}; color:#fff; border:1px solid ${borderColor}; font-size:1.13rem; padding:10px 8px; border-radius:12px 12px 0 0; text-align:center; letter-spacing:1px;">الطلاب المسجلون في الحافلة</th></tr>
      <tr style="background:${subHeaderBg};">
        <th style='border:1px solid ${borderColor}; padding:8px 10px;'>#</th>
        <th style='border:1px solid ${borderColor}; padding:8px 10px;'>اسم الطالب</th>
        <th style='border:1px solid ${borderColor}; padding:8px 10px;'>الصف</th>
      </tr>
      ${studentsRows}
    </table>
  `;

  // دمج كل الجداول في HTML واحد
  const fullHtml = `
    <div style='direction:rtl; font-family:Alexandria, Arial, sans-serif;'>
      <div style='font-size:2.1rem; font-weight:800; color:${mainColor}; margin-bottom:18px; text-align:center; letter-spacing:1px;'>${bus.name || bus.bus_number || ''}</div>
      ${busDetailsHtml}
      ${maintenanceTableHtml}
      ${studentsTableHtml}
    </div>
  `;

  exportToPrintWindow(
    [],
    [],
    `تفاصيل الحافلة: ${bus.name || bus.bus_number || ''}`,
    { customHtml: fullHtml }
  );
}

// Helper needed for date formatting in print
function formatDate(dateString) {
  if (!dateString) return 'غير محدد';
  try {
    return new Date(dateString).toLocaleDateString('en-GB');
  } catch (error) {
    return dateString;
  }
}

export function exportStudentDetailsToPrint(student, options = {}) {
  if (!student) return;

  const mainColor = '#4d4cac';
  const textColor = '#333';
  const headerBg = '#e9ecf3';
  const altRowBg = '#f6f8fb';
  const borderColor = '#d0d4e0';

  const sectionTitle = (title) => `
    <h2 style="margin: 0 0 12px; background:${mainColor}; color:#fff; padding:10px; border-radius:8px; text-align:center; font-size:1.1rem;">
      ${title}
    </h2>`;

  const tableWrap = (content) => `
    <div style="margin-bottom:24px; border-radius:10px; overflow:hidden; box-shadow:0 2px 8px rgba(0,0,0,0.05); background:#fff;">
      ${content}
    </div>`;

  const buildRow = (cells, isHeader = false, isAlt = false) => {
    const bg = isHeader ? headerBg : isAlt ? altRowBg : '#fff';
    return `<tr style="background:${bg};">${cells.map(c => `
      <td style="padding:10px; border:1px solid ${borderColor}; font-weight:${c.bold ? 'bold' : 'normal'}; text-align:${c.align || 'right'};${c.style || ''}"${c.colspan ? ` colspan=${c.colspan}` : ''}>
        ${c.text}
      </td>`).join('')}</tr>`;
  };

  const buildTable = (rows, headers = []) => `
    <table style="width:100%; border-collapse:collapse; font-size:1rem; color:${textColor};">
      ${headers.length ? buildRow(headers, true) : ''}
      ${rows.map((r, i) => buildRow(r, false, i % 2 === 0)).join('')}
    </table>`;

  // تفاصيل الطالب
  const studentTable = tableWrap(
    sectionTitle("📄 تفاصيل الطالب") +
    buildTable([
      [
        { text: 'الاسم', bold: true },
        { text: `${student.first_name || ''} ${student.second_name || ''}` },
        { text: 'الصف', bold: true },
        { text: options.className || student.className || 'غير معروف' },
      ],
      [
        { text: 'رقم الهوية', bold: true },
        { text: student.student_id || '' },
        { text: 'تاريخ الميلاد', bold: true },
        { text: student.birthdate || '' },
      ],
      [
        { text: 'تاريخ التسجيل', bold: true },
        { text: student.date_of_registration || '' },
        { text: 'العنوان', bold: true },
      { text: student.address || 'فلسطين' }
      ],
      [
        { text: 'ملاحظات', bold: true },
        { text: student.note || 'لا توجد' },
      ],
    ])
  );

  // معلومات ولي الأمر
  const parentTable = tableWrap(
    sectionTitle("👨‍👩‍👦 معلومات ولي الأمر") +
    buildTable([
      [
        { text: 'الاسم', bold: true },
        { text: student.parent_name || '' },
        { text: 'الهاتف', bold: true },
        { text: student.parent_phone || '' },
      ],
      [
        { text: 'البريد الإلكتروني', bold: true },
        { text: student.parent_email || 'غير محدد', align: 'center', colspan: 3 },
      ],
    ])
  );

  // معلومات الدفع
  const paymentTable = tableWrap(
    sectionTitle("💰 معلومات الدفع") +
    buildTable([
      [
        { text: 'إجمالي الرسوم', bold: true },
        { text: `${options.totalDue ?? ''} شيكل` },
        { text: 'المدفوع', bold: true },
        { text: `${options.paidAmount ?? ''} شيكل` },
        { text: 'المتبقي', bold: true },
        {
          text: `${options.remaining ?? ''} شيكل`,
          style: `color:${(options.remaining ?? 0) > 0 ? 'red' : 'green'}`
        },
      ],
    ])
  );

  // سجل الدفعات (مفصل)
  const detailedPaymentsTable = tableWrap(
    sectionTitle("📆 سجل دفعات الطالب (تفصيلي)") +
    buildTable(
      (Array.isArray(options.payments) && options.payments.length > 0)
        ? options.payments.map((p, idx) => {
            let details = '';
            if (p.payment_type === 'شيك' && p.cheque) {
              details = `<div style="margin-top:6px; font-size:0.97em; color:#1976d2; background:#f6f8fb; border-radius:6px; padding:6px 8px;">
                <b>تفاصيل الشيك:</b><br/>
                رقم الشيك: ${p.cheque.cheque_number || '-'}<br/>
                تاريخ الاستحقاق: ${p.cheque.cheque_date || '-'}<br/>
                اسم البنك: ${p.cheque.bank_name || '-'}<br/>
                اسم الفرع: ${p.cheque.branch_name || '-'}<br/>
                رقم البنك: ${p.cheque.bank_number || '-'}<br/>
                رقم الفرع: ${p.cheque.branch_number || '-'}<br/>
                رقم الحساب: ${p.cheque.account_number || '-'}
              </div>`;
            } else if (p.payment_type && p.payment_type.includes('تحويل')) {
              details = `<span style="color:#1976d2;">تم الدفع بواسطة تحويل بنكي</span>`;
            } else if (p.payment_type === 'نقداً') {
              details = `<span style="color:#388e3c;">تم الدفع نقداً</span>`;
            } else if (p.payment_type) {
              details = `<span style="color:#1976d2;">${p.payment_type}</span>`;
            } else {
              details = '-';
            }
            return [
              { text: p.number || '-', align: 'center' },
              { text: Number(p.amount).toLocaleString('en-US') + ' شيكل', align: 'center' },
              { text: p.payment_type || '-', align: 'center' },
              { text: formatDate(p.date), align: 'center' },
              { text: p.received ? 'تم الاستلام' : 'لم يتم الاستلام', align: 'center', style: `color:${p.received ? '#388e3c' : '#d32f2f'};font-weight:bold;` },
              { text: details, align: 'center' },
            ];
          })
        : [[{ text: 'لا يوجد دفعات سابقة', align: 'center', colspan: 6 }]],
      [
        { text: 'رقم السند', bold: true },
        { text: 'المبلغ', bold: true },
        { text: 'طريقة الدفع', bold: true },
        { text: 'التاريخ', bold: true },
        { text: 'حالة الاستلام', bold: true },
        { text: 'تفاصيل الدفع', bold: true },
      ]
    )
  );

  // معلومات الباص
  const busSection = student.is_bus_joined && options.busInfo
    ? tableWrap(
        sectionTitle("🚌 معلومات الباص") +
        buildTable([
          [
            { text: 'رقم اللوحة', bold: true },
            { text: options.busInfo.plateNumber || '' },
          ],
          [
            { text: 'اسم الباص', bold: true },
            { text: options.busInfo.name || '' },
          ],
        ])
      )
    : `<div style='margin:12px 0 24px; text-align:center; color:#999;'>🚫 الطالب غير مسجل في الباص</div>`;

  // سجل الطالب
  const historyTable = tableWrap(
    sectionTitle("📚 سجل الطالب في المدرسة") +
    buildTable(
      (Array.isArray(student.history) && student.history.length > 0)
        ? student.history.map(rec => [
            { text: rec.date || '' },
            { text: rec.event || '' },
            { text: rec.note || 'لا توجد' },
          ])
        : [[{ text: 'لا يوجد سجل للطالب', align: 'center', colspan: 3 }]],
      [
        { text: 'التاريخ', bold: true },
        { text: 'الحدث', bold: true },
        { text: 'ملاحظات', bold: true },
      ]
    )
  );

  const fullHtml = `
    <div style="padding:24px; font-family:'Alexandria', Arial, sans-serif; direction:rtl; color:${textColor}; background:#f9f9fb;">
      ${studentTable}
      ${parentTable}
      ${paymentTable}
      ${detailedPaymentsTable}
      ${busSection}
      ${historyTable}
    </div>
  `;

  exportToPrintWindow(
    [],
    [],
    `تقرير الطالب: ${student.first_name || ''} ${student.second_name || ''}`,
    { customHtml: fullHtml }
  );
}

export function exportStaffDetailsToPrint(staff, options = {}) {
  if (!staff) return;

  const mainColor = '#4d4cac';
  const textColor = '#333';
  const headerBg = '#e9ecf3';
  const altRowBg = '#f6f8fb';
  const borderColor = '#d0d4e0';

  const sectionTitle = (title) => `
    <h2 style="margin: 0 0 12px; background:${mainColor}; color:#fff; padding:10px; border-radius:8px; text-align:center; font-size:1.1rem;">
      ${title}
    </h2>`;

  const tableWrap = (content) => `
    <div style="margin-bottom:24px; border-radius:10px; overflow:hidden; box-shadow:0 2px 8px rgba(0,0,0,0.05); background:#fff;">
      ${content}
    </div>`;

  const buildRow = (cells, isHeader = false, isAlt = false) => {
    const bg = isHeader ? headerBg : isAlt ? altRowBg : '#fff';
    return `<tr style="background:${bg};">${cells.map(c => `
      <td style="padding:10px; border:1px solid ${borderColor}; font-weight:${c.bold ? 'bold' : 'normal'}; text-align:${c.align || 'right'};${c.style || ''}"${c.colspan ? ` colspan=${c.colspan}` : ''}>
        ${c.text}
      </td>`).join('')}</tr>`;
  };

  const buildTable = (rows, headers = []) => `
    <table style="width:100%; border-collapse:collapse; font-size:1rem; color:${textColor};">
      ${headers.length ? buildRow(headers, true) : ''}
      ${rows.map((r, i) => buildRow(r, false, i % 2 === 0)).join('')}
    </table>`;

  // تفاصيل الموظف
  // هنا يتم بناء التفاصيل بنفس طريقة الطالب (داخل جدول)
  const staffTable = tableWrap(
    sectionTitle("👤 تفاصيل الموظف") +
    buildTable([
      [
        { text: 'الاسم', bold: true },
        { text: `${staff.first_name || staff.name || staff.full_name || ''} ${staff.last_name || ''}` },
        { text: 'الوظيفة', bold: true },
        { text: staff.job_title || staff.role || staff.employee_type_display || 'غير محدد' },
      ],
      [
        { text: 'رقم الهوية', bold: true },
        { text: staff.employee_id || staff.national_id || staff.id_number || '' },
        { text: 'تاريخ الميلاد', bold: true },
        { text: staff.birth_date || staff.birthdate || '' },
      ],
      [
        { text: 'تاريخ التعيين', bold: true },
        { text: staff.start_date || staff.hire_date || '' },
        { text: 'ملاحظات', bold: true },
        { text: staff.note || 'لا توجد' },
      ],
      [
        { text: 'الحالة', bold: true },
        { text: staff.is_archived ? 'غير نشط' : 'نشط', style: staff.is_archived ? 'color:#d32f2f;font-weight:bold;' : 'color:#388e3c;font-weight:bold;' },
        { text: 'الراتب الأساسي', bold: true },
        { text: staff.base_salary ? `${Number(staff.base_salary).toLocaleString('en-US')} شيكل` : '—' },
      ],
    ])
  );

  // معلومات الاتصال
  const contactTable = tableWrap(
    sectionTitle("📞 معلومات الاتصال") +
    buildTable([
      [
        { text: 'الهاتف', bold: true },
        { text: staff.phone_number || staff.phone || staff.mobile || '' },
        { text: 'البريد الإلكتروني', bold: true },
        { text: staff.email || 'غير محدد' },
      ],
      [
        { text: 'العنوان', bold: true },
        { text: staff.address || 'غير محدد', align: 'center', colspan: 3 },
      ],
    ])
  );

  // سجل الرواتب
  let salaryRows = [];
  if (Array.isArray(staff.salaryHistory) && staff.salaryHistory.length > 0) {
    salaryRows = staff.salaryHistory.map((rec) => [
      { text: rec.date || '' },
      { text: rec.baseSalary ? `${rec.baseSalary} شيكل` : '' },
      { text: rec.overtimeHours ? `${rec.overtimeHours} × ${rec.overtimeRate} = ${rec.overtimeHours * rec.overtimeRate} شيكل` : '—' },
      { text: rec.bonuses && rec.bonuses.length > 0 ? rec.bonuses.map(b => `${b.type}: ${b.amount} شيكل`).join(', ') : '—' },
      {
        text:
          (rec.baseSalary || 0) +
          ((rec.overtimeHours || 0) * (rec.overtimeRate || 0)) +
          (rec.bonuses ? rec.bonuses.reduce((sum, b) => sum + b.amount, 0) : 0)
          + ' شيكل'
      },
    ]);
  } else {
    salaryRows = [[{ text: 'لا يوجد سجل رواتب', align: 'center', colspan: 5 }]];
  }
  const salaryTable = tableWrap(
    sectionTitle("💵 سجل الرواتب") +
    buildTable(
      salaryRows,
      [
        { text: 'الشهر', bold: true },
        { text: 'الراتب الأساسي', bold: true },
        { text: 'الساعات الإضافية', bold: true },
        { text: 'الحوافز', bold: true },
        { text: 'الإجمالي', bold: true },
      ]
    )
  );

  // سجل الحركات المالية
  let financeRows = [];
  if (Array.isArray(options.transactions) && options.transactions.length > 0) {
    financeRows = options.transactions.map((tx) => [
      { text: tx.date || '' },
      { text: tx.voucherNumber || '-' },
      { text: tx.credit ? Number(tx.credit).toLocaleString('en-US') : '' },
      { text: tx.debit ? Number(tx.debit).toLocaleString('en-US') : '' },
      { text: tx.description || '-' },
    ]);
  } else if (Array.isArray(options.transactions)) {
    financeRows = [[{ text: 'لا توجد حركات مالية', align: 'center', colspan: 5 }]];
  }
  const financeTable = tableWrap(
    sectionTitle("📑 الحركات المالية") +
    buildTable(
      financeRows,
      [
        { text: 'التاريخ', bold: true },
        { text: 'رقم السند', bold: true },
        { text: 'له (شيكل)', bold: true },
        { text: 'عليه (شيكل)', bold: true },
        { text: 'الوصف', bold: true },
      ]
    )
  );

  // مجاميع الحركات المالية
  let financeSummary = '';
  if (options.totalCredit !== undefined && options.totalDebit !== undefined && options.net !== undefined) {
    financeSummary = `
      <div style="margin:16px 0 24px; text-align:center; font-size:1.08rem;">
        <span style="margin-left:24px;font-weight:bold;color:#388e3c;">المجموع له: ${Number(options.totalCredit).toLocaleString('en-US')} شيكل</span>
        <span style="margin-left:24px;font-weight:bold;color:#d32f2f;">المجموع عليه: ${Number(options.totalDebit).toLocaleString('en-US')} شيكل</span>
        <span style="margin-left:24px;font-weight:bold;color:${options.net >= 0 ? '#388e3c' : '#d32f2f'};">الصافي: ${Number(options.net).toLocaleString('en-US')} شيكل</span>
      </div>
    `;
  }

  // دمج كل الأقسام
  const fullHtml = `
    <div style="padding:24px; font-family:'Alexandria', Arial, sans-serif; direction:rtl; color:${textColor}; background:#f9f9fb;">
      ${staffTable}
      ${contactTable}
      ${salaryTable}
      ${financeTable}
      ${financeSummary}
    </div>
  `;

  exportToPrintWindow(
    [],
    [],
    `تقرير الموظف: ${staff.first_name || staff.name || staff.full_name || ''} ${staff.last_name || ''}`,
    { customHtml: fullHtml }
  );
}

export function exportVoucherToPrint(voucher, options = {}) {
  if (!voucher) return;

  const mainColor = '#4d4cac';
  const borderColor = '#d3d9e6';
  const labelStyle = `font-weight:bold;color:${mainColor};min-width:110px;display:inline-block;`;
  const valueStyle = 'font-weight:bold;color:#333;';
  const textColor = '#333';
  const sectionTitle = (title) => `
    <h2 style="margin:0 0 16px;background:${mainColor};color:#fff;padding:10px;border-radius:10px;text-align:center;font-size:1.2rem;">
      ${title}
    </h2>`;

  // بيانات الشيك
  let chequeSection = '';
  if (voucher.payment_type === 'شيك' && voucher.cheque) {
    chequeSection = `
      <div style="margin-top:20px;">
        <div style="font-weight:bold;color:#1976d2;margin-bottom:8px;">تفاصيل الشيك:</div>
        <table style="width:100%;border-collapse:collapse;font-size:1rem;">
          <tr>
            <td style="${labelStyle}">رقم الشيك:</td>
            <td style="${valueStyle}">${voucher.cheque.cheque_number || '-'}</td>
            <td style="${labelStyle}">تاريخ الاستحقاق:</td>
            <td style="${valueStyle}">${voucher.cheque.cheque_date || '-'}</td>
          </tr>
          <tr>
            <td style="${labelStyle}">اسم البنك:</td>
            <td style="${valueStyle}">${voucher.cheque.bank_name || '-'}</td>
            <td style="${labelStyle}">اسم الفرع:</td>
            <td style="${valueStyle}">${voucher.cheque.branch_name || '-'}</td>
          </tr>
          <tr>
            <td style="${labelStyle}">رقم البنك:</td>
            <td style="${valueStyle}">${voucher.cheque.bank_number || '-'}</td>
            <td style="${labelStyle}">رقم الفرع:</td>
            <td style="${valueStyle}">${voucher.cheque.branch_number || '-'}</td>
          </tr>
          <tr>
            <td style="${labelStyle}">رقم الحساب:</td>
            <td colspan="3" style="${valueStyle}">${voucher.cheque.account_number || '-'}</td>
          </tr>
        </table>
      </div>
    `;
  }

  let visaSection = '';
  if (voucher.payment_type === 'فيزا' && voucher.visa) {
    visaSection = `
      <div style="margin-top:20px;">
        <div style="font-weight:bold;color:#1976d2;margin-bottom:8px;">تفاصيل الفيزا:</div>
        <table style="width:100%;border-collapse:collapse;font-size:1rem;">
          <tr>
            <td style="${labelStyle}">رقم الإيصال:</td>
            <td style="${valueStyle}">${voucher.visa.receipt_number || '-'}</td>
            <td style="${labelStyle}">الوصف:</td>
            <td style="${valueStyle}">${voucher.visa.description || '-'}</td>
          </tr>
        </table>
      </div>
    `;
  }

  // حوالة بنكية
  let transferSection = '';
  if (voucher.payment_type?.includes('تحويل')) {
    transferSection = `
      <div style="margin-top:20px;">
        <div style="font-weight:bold;color:#1976d2;margin-bottom:8px;">تفاصيل الحوالة البنكية:</div>
        <div style="color:#333;font-size:1rem;">تم الدفع بواسطة حوالة بنكية.</div>
      </div>
    `;
  }

  // ترويسة المدرسة
  const schoolInfo = JSON.parse(localStorage.getItem('schoolInfo')) || {};
  const userName = getCurrentUserName();
  const schoolPhone = getSchoolPhone(schoolInfo);
  const header = `
    <div style="width:100%;text-align:center;padding:24px 16px;background:#f4f6fa;border-radius:16px 16px 0 0;border-bottom:3px solid ${borderColor};">
      <div style="font-size:2.2rem;font-weight:800;color:${mainColor};margin-bottom:8px;">${schoolInfo.name || ''}</div>
      <div style="font-size:1.05rem;color:#444;">${'العنوان: ' + schoolInfo.address || ''}</div>
      <div style="font-size:1rem;color:#666;">${schoolPhone ? 'هاتف المدرسة: ' + schoolPhone : ''}</div>
      <div style="font-size:1rem;color:#666;">${schoolInfo.email ? 'البريد الإلكتروني: ' + schoolInfo.email : ''}</div>
    </div>
  `;

  const voucherType = options.type || (voucher.type === 'موظف' ? 'سند صرف' : 'سند قبض');

  const table = `
    <table style="width:100%;margin:24px 0 0 0;border-collapse:collapse;font-size:1rem;color:${textColor};border:1px solid ${borderColor};border-radius:12px;overflow:hidden;box-shadow:0 2px 10px rgba(0,0,0,0.04);background:#fff;">
      <tr style="background:#f9f9fb;">
        <td style="${labelStyle}">نوع السند:</td>
        <td style="${valueStyle}">${voucherType}</td>
        <td style="${labelStyle}">رقم السند:</td>
        <td style="${valueStyle}">${voucher.number || '-'}</td>
      </tr>
      <tr>
        <td style="${labelStyle}">الاسم:</td>
        <td style="${valueStyle}">${voucher.target_name || voucher.student_name || '-'}</td>
        <td style="${labelStyle}">المبلغ:</td>
        <td style="${valueStyle}">${Number(voucher.amount).toLocaleString('en-US')} شيكل</td>
      </tr>
      <tr style="background:#f9f9fb;">
        <td style="${labelStyle}">طريقة الدفع:</td>
        <td style="${valueStyle}">${voucher.payment_type || '-'}</td>
        <td style="${labelStyle}">التاريخ:</td>
        <td style="${valueStyle}">${voucher.date || '-'}</td>
      </tr>
      <tr>
        <td style="${labelStyle}">الوقت:</td>
        <td style="${valueStyle}">${voucher.time_display || voucher.time || '-'}</td>
        <td style="${labelStyle}">السبب / البيان:</td>
        <td style="${valueStyle}">${voucher.reason || '-'}</td>
      </tr>
      <tr>
        <td style="${labelStyle}">أنشئ بواسطة:</td>
        <td style="${valueStyle}">${voucher.created_by_name || '-'}</td>
      </tr>
    </table>
  `;

  const html = `
    <html dir="rtl">
      <head>
        <title>${voucherType}</title>
        <style>
          body {
            font-family: 'Alexandria', Arial, sans-serif;
            background: #f4f6fa;
            margin: 0;
            padding: 32px;
            color: ${textColor};
          }
          h2 {
            font-size: 1.8rem;
            font-weight: 700;
            margin-bottom: 20px;
            text-align: center;
            letter-spacing: 1px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          td {
            padding: 10px 8px;
          }
          @media print {
            body { background: #fff; padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        ${header}
        ${sectionTitle(voucherType)}
        ${table}
        ${chequeSection}
        ${transferSection}
        ${visaSection}
        <div style="margin-top:36px;text-align:left;color:#888;font-size:0.95rem;">
          <span>تاريخ الطباعة: ${new Date().toLocaleDateString('en-GB')}</span><br/>
          <span>المستخدم: ${userName || '-'} </span>
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open('', '', 'width=900,height=650');
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = function() {
    setTimeout(() => printWindow.print(), 350); // انتظر حتى يتم تحميل الصور
  };
}

export function exportRecipientToPrint(recipient) {
  if (!recipient) return;

  const mainColor = '#4d4cac';
  const borderColor = '#d3d9e6';
  const labelStyle = `font-weight:bold;color:${mainColor};min-width:110px;display:inline-block;`;
  const valueStyle = 'font-weight:bold;color:#333;';
  const textColor = '#333';

  // School header
  const schoolInfo = JSON.parse(localStorage.getItem('schoolInfo')) || {};
  const userName = getCurrentUserName();
  const schoolPhone = getSchoolPhone(schoolInfo);
  const header = `
    <div style="width:100%;text-align:center;padding:24px 16px;background:#f4f6fa;border-radius:16px 16px 0 0;border-bottom:3px solid ${borderColor};">
      ${schoolInfo.name ? `<div style="font-size:2.2rem;font-weight:800;color:${mainColor};margin-bottom:8px;">${schoolInfo.name}</div>` : ''}
      ${schoolInfo.address ? `<div style="font-size:1.05rem;color:#444;">العنوان: ${schoolInfo.address}</div>` : ''}
      ${schoolPhone ? `<div style="font-size:1rem;color:#666;">هاتف المدرسة: ${schoolPhone}</div>` : ''}
      ${schoolInfo.email ? `<div style="font-size:1rem;color:#666;">البريد الإلكتروني: ${schoolInfo.email}</div>` : ''}
    </div>
  `;

  // Build table rows dynamically based on available data
  const buildTableRows = () => {
    let rows = '';
    
    // Row 1: Receipt number and Student name
    if (recipient.number || recipient.student_name) {
      rows += `<tr style="background:#f9f9fb;">`;
      if (recipient.number) {
        rows += `<td style="${labelStyle}">رقم السند:</td><td style="${valueStyle}">${recipient.number}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      if (recipient.student_name) {
        rows += `<td style="${labelStyle}">اسم الطالب:</td><td style="${valueStyle}">${recipient.student_name}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      rows += `</tr>`;
    }

    // Row 2: Parent name and Parent phone
    if (recipient.parent_name || recipient.parent_phone) {
      rows += `<tr>`;
      if (recipient.parent_name) {
        rows += `<td style="${labelStyle}">اسم ولي الأمر:</td><td style="${valueStyle}">${recipient.parent_name}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      if (recipient.parent_phone) {
        rows += `<td style="${labelStyle}">هاتف ولي الأمر:</td><td style="${valueStyle}">${recipient.parent_phone}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      rows += `</tr>`;
    }

    // Row 3: Class and Payment type
    if (recipient.class_name || recipient.payment_type) {
      rows += `<tr style="background:#f9f9fb;">`;
      if (recipient.class_name) {
        rows += `<td style="${labelStyle}">الصف:</td><td style="${valueStyle}">${recipient.class_name}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      if (recipient.payment_type) {
        rows += `<td style="${labelStyle}">طريقة الدفع:</td><td style="${valueStyle}">${recipient.payment_type}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      rows += `</tr>`;
    }

    // Row 4: Date and Time
    if (recipient.date || recipient.time_display || recipient.time) {
      rows += `<tr>`;
      if (recipient.date) {
        rows += `<td style="${labelStyle}">التاريخ:</td><td style="${valueStyle}">${recipient.date}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      if (recipient.time_display || recipient.time) {
        rows += `<td style="${labelStyle}">الوقت:</td><td style="${valueStyle}">${recipient.time_display || recipient.time}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      rows += `</tr>`;
    }

    // Row 5: Amount and Receipt status
    if (recipient.amount !== undefined || recipient.received !== undefined) {
      rows += `<tr style="background:#f9f9fb;">`;
      if (recipient.amount !== undefined) {
        rows += `<td style="${labelStyle}">المبلغ:</td><td style="${valueStyle}">${Number(recipient.amount).toLocaleString('en-US')} شيكل</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      if (recipient.received !== undefined) {
        rows += `<td style="${labelStyle}">حالة الاستلام:</td><td style="${valueStyle}">${recipient.received ? 'تم الاستلام' : 'لم يتم الاستلام'}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      rows += `</tr>`;
    }

    // Row 5.5: Total school fee and Remaining balance after this receipt
    if (recipient.total_school_fee !== undefined || recipient.remaining_amount !== undefined) {
      rows += `<tr>`;
      if (recipient.total_school_fee !== undefined) {
        rows += `<td style="${labelStyle}">إجمالي الرسوم:</td><td style="${valueStyle}">${Number(recipient.total_school_fee).toLocaleString('en-US')} شيكل</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      if (recipient.remaining_amount !== undefined) {
        rows += `<td style="${labelStyle}">الباقي:</td><td style="font-weight:bold;color:#c0392b;">${Number(recipient.remaining_amount).toLocaleString('en-US')} شيكل</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      rows += `</tr>`;
    }

    // Row 6: School year and Student ID
    if (recipient.school_year_label || recipient.student_id) {
      rows += `<tr>`;
      if (recipient.school_year_label) {
        rows += `<td style="${labelStyle}">سنة دراسية:</td><td style="${valueStyle}">${recipient.school_year_label}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      if (recipient.student_id) {
        rows += `<td style="${labelStyle}">رقم هوية الطالب:</td><td style="${valueStyle}">${recipient.student_id}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      rows += `</tr>`;
    }

    // Row 7: Account and Created at
    if (recipient.updated_at || recipient.created_at) {
      rows += `<tr style="background:#f9f9fb;">`;
      if (recipient.created_at) {
        rows += `<td style="${labelStyle}">تاريخ الإنشاء:</td><td style="${valueStyle}">${new Date(recipient.created_at).toLocaleString('en-GB')}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      if (recipient.updated_at) {
        rows += `<td style="${labelStyle}">آخر تحديث:</td><td style="${valueStyle}">${new Date(recipient.updated_at).toLocaleString('en-GB')}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      rows += `</tr>`;
    }

    // Row 8: Updated at and Created by
    if (recipient.created_by_name) {
      rows += `<tr>`;
      if (recipient.created_by_name) {
        rows += `<td style="${labelStyle}">أنشئ بواسطة:</td><td style="${valueStyle}">${recipient.created_by_name}</td>`;
      } else {
        rows += `<td colspan="2"></td>`;
      }
      rows += `</tr>`;
    }

    return rows;
  };

  // Cheque details section - Only show if cheque payment and has cheque data
  let chequeSection = '';
  if (recipient.payment_type === 'شيك' && recipient.cheque) {
    chequeSection = `
      <div style="margin-top:20px;">
        <div style="font-weight:bold;color:#1976d2;margin-bottom:8px;">تفاصيل الشيك:</div>
        <table style="width:100%;border-collapse:collapse;font-size:1rem;">
          ${recipient.cheque.cheque_number || recipient.cheque.cheque_date ? `
            <tr>
              ${recipient.cheque.cheque_number ? `
                <td style="${labelStyle}">رقم الشيك:</td>
                <td style="${valueStyle}">${recipient.cheque.cheque_number}</td>
              ` : '<td colspan="2"></td>'}
              ${recipient.cheque.cheque_date ? `
                <td style="${labelStyle}">تاريخ الاستحقاق:</td>
                <td style="${valueStyle}">${recipient.cheque.cheque_date}</td>
              ` : '<td colspan="2"></td>'}
            </tr>
          ` : ''}
          ${recipient.cheque.bank_name || recipient.cheque.branch_name ? `
            <tr>
              ${recipient.cheque.bank_name ? `
                <td style="${labelStyle}">اسم البنك:</td>
                <td style="${valueStyle}">${recipient.cheque.bank_name}</td>
              ` : '<td colspan="2"></td>'}
              ${recipient.cheque.branch_name ? `
                <td style="${labelStyle}">اسم الفرع:</td>
                <td style="${valueStyle}">${recipient.cheque.branch_name}</td>
              ` : '<td colspan="2"></td>'}
            </tr>
          ` : ''}
          ${recipient.cheque.bank_number || recipient.cheque.branch_number ? `
            <tr>
              ${recipient.cheque.bank_number ? `
                <td style="${labelStyle}">رقم البنك:</td>
                <td style="${valueStyle}">${recipient.cheque.bank_number}</td>
              ` : '<td colspan="2"></td>'}
              ${recipient.cheque.branch_number ? `
                <td style="${labelStyle}">رقم الفرع:</td>
                <td style="${valueStyle}">${recipient.cheque.branch_number}</td>
              ` : '<td colspan="2"></td>'}
            </tr>
          ` : ''}
          ${recipient.cheque.account_number ? `
            <tr>
              <td style="${labelStyle}">رقم الحساب:</td>
              <td colspan="3" style="${valueStyle}">${recipient.cheque.account_number}</td>
            </tr>
          ` : ''}
          ${recipient.cheque.description ? `
            <tr>
              <td style="${labelStyle}">وصف الشيك:</td>
              <td colspan="3" style="${valueStyle}">${recipient.cheque.description}</td>
            </tr>
          ` : ''}
        </table>
      </div>
    `;
  }

  // Visa details section - Only show if visa payment and has visa data
  let visaSection = '';
  if (recipient.payment_type === 'فيزا' && recipient.visa) {
    visaSection = `
      <div style="margin-top:20px;">
        <div style="font-weight:bold;color:#9c27b0;margin-bottom:8px;">تفاصيل الفيزا:</div>
        <table style="width:100%;border-collapse:collapse;font-size:1rem;">
          ${recipient.visa.receipt_number ? `
            <tr>
              <td style="${labelStyle}">رقم الإيصال:</td>
              <td style="${valueStyle}">${recipient.visa.receipt_number}</td>
            </tr>
          ` : ''}
          ${recipient.visa.description ? `
            <tr>
              <td style="${labelStyle}">الوصف:</td>
              <td style="${valueStyle}">${recipient.visa.description}</td>
            </tr>
          ` : ''}
        </table>
      </div>
    `;
  }

  // Main table
  const table = `
    <table style="width:100%;margin:24px 0 0 0;border-collapse:collapse;font-size:1rem;color:${textColor};border:1px solid ${borderColor};border-radius:12px;overflow:hidden;box-shadow:0 2px 10px rgba(0,0,0,0.04);background:#fff;">
      ${buildTableRows()}
    </table>
  `;

  const html = `
    <html dir="rtl">
      <head>
        <title>سند قبض</title>
        <style>
          body {
            font-family: 'Alexandria', Arial, sans-serif;
            background: #f4f6fa;
            margin: 0;
            padding: 32px;
            color: ${textColor};
          }
          h2 {
            font-size: 1.8rem;
            font-weight: 700;
            margin-bottom: 20px;
            text-align: center;
            letter-spacing: 1px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          td {
            padding: 10px 8px;
          }
          @media print {
            body { background: #fff; padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        ${header}
        <h2>سند قبض</h2>
        ${table}
        ${chequeSection}
        ${visaSection}
        <div style="margin-top:36px;text-align:left;color:#888;font-size:0.95rem;">
          <span>تاريخ الطباعة: ${new Date().toLocaleDateString('en-GB')}</span><br/>
          <span>المستخدم: ${userName || '-'} </span>
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open('', '', 'width=900,height=650');
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = function() {
    setTimeout(() => printWindow.print(), 350);
  };
}
